import { useParams, Link } from "react-router-dom";
import {
  useIncident,
} from "@crm/api";
import {
  ArrowLeft,
  Clock,
  CheckCircle,
} from "lucide-react";
import { cn } from "@crm/utils";
import { TicketChat } from "../components/TicketChat";

const statusConfig: Record<
  string,
  { label: string; color: string; description: string }
> = {
  new: {
    label: "Submitted",
    color: "bg-blue-100 text-blue-800",
    description: "Your ticket has been received",
  },
  assigned: {
    label: "Assigned",
    color: "bg-yellow-100 text-yellow-800",
    description: "A technician has been assigned",
  },
  in_progress: {
    label: "In Progress",
    color: "bg-purple-100 text-purple-800",
    description: "We're working on your issue",
  },
  on_hold: {
    label: "Waiting",
    color: "bg-gray-100 text-gray-800",
    description: "Waiting for additional information",
  },
  resolved: {
    label: "Resolved",
    color: "bg-green-100 text-green-800",
    description: "Your issue has been resolved",
  },
  closed: {
    label: "Closed",
    color: "bg-green-100 text-green-800",
    description: "This ticket is closed",
  },
};

const categoryLabels: Record<string, string> = {
  hardware: "Hardware Issue",
  software: "Software Issue",
  network: "Network Issue",
  security: "Security Issue",
  access: "Access Request",
  other: "Other",
};

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: ticket, isLoading, error } = useIncident(id!);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">Failed to load ticket</p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to My Tickets
          </Link>
        </div>
      </div>
    );
  }

  const status = statusConfig[ticket.status] || statusConfig.new;
  const isResolved = ["resolved", "closed"].includes(ticket.status);

  return (
    <div className="mx-auto max-w-3xl p-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to My Tickets
        </Link>

        {/* Status Banner */}
        <div
          className={cn(
            "mb-4 rounded-lg p-4",
            isResolved
              ? "bg-green-50 border border-green-200"
              : "bg-blue-50 border border-blue-200"
          )}
        >
          <div className="flex items-center gap-3">
            {isResolved ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <Clock className="h-6 w-6 text-blue-600" />
            )}
            <div>
              <p
                className={cn(
                  "font-semibold",
                  isResolved ? "text-green-800" : "text-blue-800"
                )}
              >
                {status.label}
              </p>
              <p
                className={cn(
                  "text-sm",
                  isResolved ? "text-green-600" : "text-blue-600"
                )}
              >
                {status.description}
              </p>
            </div>
          </div>
        </div>

        <h1 className="text-2xl font-semibold text-foreground">
          {ticket.title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Submitted {new Date(ticket.created_at).toLocaleString()} •{" "}
          {categoryLabels[ticket.category]}
        </p>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Description */}
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            Your Issue
          </h2>
          <p className="whitespace-pre-wrap text-foreground">
            {ticket.description || "No description provided."}
          </p>
        </div>

        {/* Resolution Notes */}
        {ticket.resolution_notes && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-6">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-green-800">
              <CheckCircle className="h-5 w-5" />
              Resolution
            </h2>
            <p className="whitespace-pre-wrap text-green-700">
              {ticket.resolution_notes}
            </p>
          </div>
        )}

        {/* Assigned To */}
        {ticket.assigned_to && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              Assigned Technician
            </h2>
            <p className="font-medium text-foreground">
              {ticket.assigned_to.first_name} {ticket.assigned_to.last_name}
            </p>
          </div>
        )}

        {/* Real-time Chat */}
        <TicketChat ticketId={id!} isResolved={isResolved} />
      </div>
    </div>
  );
}
